import { Injectable } from '@angular/core';
import { app, authentication } from '@microsoft/teams-js';
import { couldBeInsideTeams, isInTeams, setInTeams } from './teams-runtime';

/**
 * Wraps the Microsoft Teams JS SDK.
 *
 * Lifecycle:
 *  - `detect()` is called once from main.ts BEFORE the Angular app bootstraps.
 *    It probes whether we are hosted inside Teams and records the result in the
 *    shared teams-runtime flag so provider factories / guards can branch.
 *  - After bootstrap, components/services use `getContext()`, `getSsoToken()`,
 *    and `notifyAppLoaded()`.
 */
@Injectable({ providedIn: 'root' })
export class TeamsService {
  private context: app.Context | null = null;

  /** True when the app is running inside the Teams client. */
  get isInTeams(): boolean {
    return isInTeams();
  }

  /**
   * Probe for the Teams host and initialize the SDK. Resolves to whether we are
   * inside Teams. Safe to call in a normal browser: it returns false quickly
   * without throwing. Intended to be awaited once during bootstrap.
   */
  static async detect(timeoutMs = 2000): Promise<boolean> {
    if (!couldBeInsideTeams()) {
      setInTeams(false);
      return false;
    }
    try {
      // app.initialize() never resolves outside a Teams host, so race a timeout.
      await Promise.race([
        app.initialize(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('teams-init-timeout')), timeoutMs)
        ),
      ]);
      setInTeams(true);
      return true;
    } catch {
      setInTeams(false);
      return false;
    }
  }

  /** Cached Teams context (user identity, theme, locale, etc.). */
  async getContext(): Promise<app.Context | null> {
    if (!this.isInTeams) return null;
    if (!this.context) {
      this.context = await app.getContext();
    }
    return this.context;
  }

  /**
   * Acquire the Teams SSO token for the current user. This token's audience is
   * our app registration (see manifest webApplicationInfo.resource); it is then
   * exchanged for an API access token by the backend OBO endpoint.
   */
  async getSsoToken(): Promise<string> {
    if (!this.isInTeams) {
      throw new Error('getSsoToken called outside Teams');
    }
    return authentication.getAuthToken();
  }

  /** Tell the Teams host the tab finished loading successfully. */
  notifyAppLoaded(): void {
    if (!this.isInTeams) return;
    try {
      app.notifySuccess();
    } catch {
      // notifySuccess throws if the host already timed out; non-fatal.
    }
  }
}
